import React, { useEffect, useState, useMemo } from "react";
import { X, Send, CheckCircle2, Search, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ShareClipModal — picker dialog that lets the clip creator share their clip
 * with eligible recipients (teammates/coach for coach + player_parent roles;
 * org-wide for org_admin; etc. — backend re-validates scope).
 */
const ShareClipModal = ({ clip, onClose, onShared }) => {
  const { api, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api()
      .get("/users")
      .then((r) => {
        const already = new Set(clip.shared_with || []);
        const list = r.data
          .filter((u) => u.id !== user.id)
          .map((u) => ({ ...u, already_shared: already.has(u.id) }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setUsers(list);
      })
      .catch(() => setError("Failed to load people you can share with"))
      .finally(() => setLoading(false));
  }, [api, user.id, clip.shared_with]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q)
    );
  }, [search, users]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!selected.size) {
      setError("Pick at least one person.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api().post(`/clips/${clip.id}/share`, { user_ids: Array.from(selected) });
      setDone({ added: r.data?.added?.length || 0 });
      if (onShared) onShared(r.data?.clip);
      setTimeout(() => onClose(), 1400);
    } catch (e) {
      setError(e.response?.data?.detail || "Share failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="share-clip-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: 16,
      }}
    >
      <div
        className="glass-card"
        style={{ padding: 0, width: "min(96vw, 480px)", maxHeight: "80vh", display: "flex", flexDirection: "column" }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid var(--dark-border)",
          }}
        >
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Outfit', sans-serif", color: "#fff" }}>
              Share clip
            </h3>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
              "{clip.title}"
            </p>
          </div>
          <button
            data-testid="share-clip-close"
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#888", cursor: "pointer", padding: 4 }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        {done ? (
          <div data-testid="share-clip-success" style={{ padding: 32, textAlign: "center" }}>
            <CheckCircle2 size={40} style={{ color: "#4caf50", marginBottom: 10 }} />
            <p style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>
              Shared with {done.added} {done.added === 1 ? "person" : "people"}
            </p>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>
              They'll see it under their "Shared" tab.
            </p>
          </div>
        ) : (
          <>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--dark-border)" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#888",
                  }}
                />
                <input
                  data-testid="share-clip-search"
                  placeholder="Search teammates, coach, parents…"
                  className="input-field"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 32 }}
                />
              </div>
            </div>

            <div style={{ overflowY: "auto", flex: 1, padding: 8 }}>
              {loading ? (
                <p style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 13 }}>Loading…</p>
              ) : filtered.length === 0 ? (
                <div style={{ padding: 28, textAlign: "center" }}>
                  <Users size={28} style={{ color: "#666", marginBottom: 8 }} />
                  <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                    {users.length === 0 ? "No teammates available to share with yet." : "No matches."}
                  </p>
                </div>
              ) : (
                filtered.map((u) => {
                  const isSel = selected.has(u.id);
                  return (
                    <label
                      key={u.id}
                      data-testid={`share-recipient-${u.id}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        borderRadius: 10,
                        cursor: "pointer",
                        background: isSel ? "rgba(0,229,255,0.10)" : "transparent",
                        border: `1px solid ${isSel ? "var(--cyan)" : "transparent"}`,
                        marginBottom: 4,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggle(u.id)}
                        style={{ accentColor: "var(--cyan)", width: 16, height: 16 }}
                      />
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: "var(--cyan)",
                          color: "#000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 14,
                        }}
                      >
                        {(u.name || u.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {u.name || u.email}
                        </p>
                        <p style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                          {(u.role || "").replace("_", " ")}
                          {u.already_shared ? " · already shared" : ""}
                        </p>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            {error && (
              <p data-testid="share-clip-error" style={{ color: "#f44336", fontSize: 13, padding: "0 20px 8px" }}>
                {error}
              </p>
            )}

            <div
              style={{
                display: "flex",
                gap: 10,
                padding: "12px 16px",
                borderTop: "1px solid var(--dark-border)",
              }}
            >
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: 10,
                  background: "transparent",
                  border: "1px solid var(--dark-border)",
                  borderRadius: 10,
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                data-testid="share-clip-submit"
                onClick={submit}
                disabled={submitting || !selected.size}
                className="btn-primary"
                style={{
                  flex: 1,
                  padding: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  opacity: submitting || !selected.size ? 0.6 : 1,
                  cursor: submitting || !selected.size ? "not-allowed" : "pointer",
                }}
              >
                <Send size={14} /> Share ({selected.size})
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareClipModal;
